<template>
  <div class="dashboard-contenedor">
    <SlideBar @cambiar-estado="actualizarSidebar" />

    <div class="contenido" :class="{ 'contenido-expandido': sidebarCerrado }">

      <!-- Barra de herramientas: búsqueda + toggle -->
      <div class="barra-herramientas">
        <input
          v-if="!vistaCalendario"
          v-model="busqueda"
          type="text"
          class="form-control bht-input"
          placeholder="Buscar paciente por nombre o diagnóstico..."
        >
        <span v-else class="bht-spacer"></span>
        <div class="switch-tipo-paciente">
          <button :class="{ active: tipoPacienteVista === 'huesped' }" @click="tipoPacienteVista = 'huesped'">Huéspedes</button>
          <button :class="{ active: tipoPacienteVista === 'colaborador' }" @click="tipoPacienteVista = 'colaborador'">Colaboradores</button>
        </div>
        <button class="btn-vista" @click="vistaCalendario = !vistaCalendario">
          <span class="material-icons">{{ vistaCalendario ? 'list' : 'calendar_month' }}</span>
          {{ vistaCalendario ? 'Vista Lista' : 'Vista Calendario' }}
        </button>
      </div>

      <!-- ─── VISTA LISTA ─────────────────────────────────────── -->
      <template v-if="!vistaCalendario">
        <p v-if="cargando" class="mensaje-cargando">Cargando pacientes...</p>
        <p v-else-if="pacientesFiltrados.length === 0" class="mensaje-vacio">No hay pacientes asignados.</p>

        <div v-else class="master-contenedor">
          <div class="tabla-wrapper">
            <table class="tabla-pacientes">
              <thead>
                <tr>
                  <th>Nombre Completo</th>
                  <th>Médico</th>
                  <th>Hotel</th>
                  <th>Género</th>
                  <th>País</th>
                  <th>Folio</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="paciente in pacientesPaginados"
                  :key="paciente.folio"
                  :class="{ 'fila-activa': pacienteSeleccionado && pacienteSeleccionado.folio === paciente.folio }"
                  @click="seleccionar(paciente)"
                >
                  <td>{{ paciente.nombre }} {{ paciente.apellido_paterno }} {{ paciente.apellido_materno }}</td>
                  <td>{{ nombreMedico(paciente.id_doctor) }}</td>
                  <td>{{ paciente.nombre_hotel || '—' }}</td>
                  <td>{{ paciente.genero }}</td>
                  <td>{{ paciente.pais }}</td>
                  <td>{{ paciente.folio }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginación -->
          <div class="paginacion">
            <button class="btn-pagina" :disabled="paginaActual === 1" @click="paginaActual--">
              <span class="material-icons">chevron_left</span>
            </button>
            <button
              v-for="pagina in totalPaginas"
              :key="pagina"
              class="btn-pagina"
              :class="{ 'btn-pagina-activa': pagina === paginaActual }"
              @click="paginaActual = pagina"
            >
              {{ pagina }}
            </button>
            <button class="btn-pagina" :disabled="paginaActual === totalPaginas" @click="paginaActual++">
              <span class="material-icons">chevron_right</span>
            </button>
            <span class="paginacion-info">
              {{ (paginaActual - 1) * porPagina + 1 }}–{{ Math.min(paginaActual * porPagina, pacientesFiltrados.length) }}
              de {{ pacientesFiltrados.length }}
            </span>
          </div>

          <!-- DETAIL: Panel inferior -->
          <transition name="detalle-fade">
            <div v-if="pacienteSeleccionado" class="detalle-panel">
              <div class="detalle-encabezado">
                <h4 v-if="!editando">
                  {{ pacienteSeleccionado.nombre }}
                  {{ pacienteSeleccionado.apellido_paterno }}
                  {{ pacienteSeleccionado.apellido_materno }}
                  <span class="detalle-folio">Folio: {{ pacienteSeleccionado.folio }}</span>
                  <span v-if="pacienteSeleccionado.oculto" class="detalle-oculto-tag">Oculto</span>
                </h4>
                <div v-else class="detalle-nombre-edicion">
                  <input v-model="formularioEdicion.nombre" class="form-control" placeholder="Nombre">
                  <input v-model="formularioEdicion.apellido_paterno" class="form-control" placeholder="Apellido paterno">
                  <input v-model="formularioEdicion.apellido_materno" class="form-control" placeholder="Apellido materno">
                  <span class="detalle-folio">Folio: {{ pacienteSeleccionado.folio }}</span>
                </div>
                <div style="display:flex; gap:8px;">
                  <!-- Botón Ocultar/Mostrar expediente (solo administradores) -->
                  <button
                    v-if="esAdministrador"
                    class="btn-editar-detalle"
                    @click="toggleOcultarExpediente"
                    :title="pacienteSeleccionado.oculto ? 'Mostrar expediente a aliados' : 'Ocultar expediente a aliados'"
                  >
                    <span class="material-icons">{{ pacienteSeleccionado.oculto ? 'visibility' : 'visibility_off' }}</span>
                  </button>
                  <!-- Botón Editar (solo administradores) -->
                  <button
                    v-if="esAdministrador && !editando"
                    class="btn-editar-detalle"
                    @click="iniciarEdicion"
                    title="Editar datos de consulta"
                  >
                    <span class="material-icons">edit</span>
                  </button>
                  <button class="btn-cerrar-detalle" @click="pacienteSeleccionado = null">
                    <span class="material-icons">close</span>
                  </button>
                </div>
              </div>

              <div class="detalle-cuerpo">
                <!-- ... (secciones fijas como Hotel, Signos Vitales, Historial) ... -->
                <div v-if="pacienteSeleccionado.nombre_hotel || editando" class="detalle-seccion">
                  <h5>Hotel</h5>
                  <p v-if="!editando" class="detalle-observaciones">{{ pacienteSeleccionado.nombre_hotel }}</p>
                  <input v-else v-model="formularioEdicion.nombre_hotel" class="form-control" placeholder="Nombre del hotel">
                </div>

                <div class="detalle-seccion">
                  <h5>Signos Vitales</h5>
                  <div class="detalle-grid">
                    <div><strong>Presión Arterial</strong><span>{{ pacienteSeleccionado.presion }}</span></div>
                    <div><strong>Frecuencia Cardíaca</strong><span>{{ pacienteSeleccionado.frecuencia }} BPM</span></div>
                    <div><strong>Temperatura</strong><span>{{ pacienteSeleccionado.temperatura }} °C</span></div>
                    <div><strong>Saturación O₂</strong><span>{{ pacienteSeleccionado.saturacion }}%</span></div>
                  </div>
                </div>

                <div class="detalle-seccion">
                  <h5>Historial Médico</h5>
                  <div class="detalle-grid">
                    <div><strong>Alergias</strong><span>{{ pacienteSeleccionado.alergias }}</span></div>
                    <div><strong>Enfermedades Preexistentes</strong><span>{{ pacienteSeleccionado.enfermedades }}</span></div>
                    <div><strong>Medicación Actual</strong><span>{{ pacienteSeleccionado.medicacion }}</span></div>
                  </div>
                </div>

                <!-- ═══ SECCIÓN CONSULTA (EDITABLE SOLO ADMIN) ═══ -->
                <div class="detalle-seccion">
                  <h5>Consulta</h5>
                  <div class="detalle-grid detalle-grid-2col">
                    <div>
                      <strong>Síntomas</strong>
                      <span v-if="!editando">{{ pacienteSeleccionado.sintomas }}</span>
                      <textarea
                        v-else
                        v-model="formularioEdicion.sintomas"
                        class="form-control"
                        rows="2"
                      ></textarea>
                    </div>
                    <div>
                      <strong>Diagnóstico</strong>
                      <span v-if="!editando">{{ pacienteSeleccionado.diagnostico }}</span>
                      <textarea
                        v-else
                        v-model="formularioEdicion.diagnostico"
                        class="form-control"
                        rows="2"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <div class="detalle-seccion" v-if="pacienteSeleccionado.observaciones || editando">
                  <h5>Observaciones</h5>
                  <p v-if="!editando" class="detalle-observaciones">{{ pacienteSeleccionado.observaciones }}</p>
                  <textarea
                    v-else
                    v-model="formularioEdicion.observaciones"
                    class="form-control"
                    rows="3"
                  ></textarea>
                </div>

                <!-- Botones de acción en modo edición -->
                <div v-if="editando" class="detalle-acciones-edicion">
                  <button class="btn-cancelar" @click="cancelarEdicion">Cancelar</button>
                  <button class="btn-guardar" @click="guardarEdicion" :disabled="guardando">
                    {{ guardando ? 'Guardando...' : 'Guardar cambios' }}
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </template>

      <!-- ─── VISTA CALENDARIO ───────────────────────────────── -->
      <div v-else class="calendario-wrapper">
        <FullCalendar :options="calendarOptions" />
      </div>

      <!-- Modal paciente (calendario) ... -->
      <transition name="modal-fade">
        <div v-if="pacienteModal" class="modal-paciente-overlay" @click.self="pacienteModal = null">
          <!-- ... mismo contenido que antes ... -->
          <div class="modal-paciente">
            <div class="modal-paciente-header">
              <h4>
                {{ pacienteModal.nombre }}
                {{ pacienteModal.apellido_paterno }}
                {{ pacienteModal.apellido_materno }}
                <span class="detalle-folio">Folio: {{ pacienteModal.folio }}</span>
              </h4>
              <button class="btn-cerrar-detalle" @click="pacienteModal = null">
                <span class="material-icons">close</span>
              </button>
            </div>
            <div class="detalle-cuerpo modal-scroll">
              <!-- ... mismo contenido estático ... -->
            </div>
          </div>
        </div>
      </transition>

    </div>

    <button class="btn-scroll" @click="toggleScroll">
      <span class="material-icons">{{ estaEnElFinal ? 'arrow_upward' : 'arrow_downward' }}</span>
    </button>
  </div>
</template>

<script>
import SlideBar from '@/components/SlideBar.vue';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import {
  getConsultas,
  getMe,
  getUsuarios,
  updateConsulta,
} from '@/utils/api';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';

export default {
  name: 'DashboardPacientes',
  components: { SlideBar, FullCalendar },
  data() {
    return {
      sidebarCerrado: false,
      busqueda: '',
      pacienteSeleccionado: null,
      pacientes: [],
      cargando: true,
      estaEnElFinal: false,
      paginaActual: 1,
      porPagina: 10,
      vistaCalendario: false,
      pacienteModal: null,
      tipoPacienteVista: 'huesped',
      doctoresPorId: {},
      // Edición
      editando: false,
      guardando: false,
      formularioEdicion: {
        nombre: '',
        apellido_paterno: '',
        apellido_materno: '',
        nombre_hotel: '',
        sintomas: '',
        diagnostico: '',
        observaciones: '',
      },
      // Usuario actual y permisos
      esAdministrador: false,
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: esLocale,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek',
        },
        events: [],
        eventColor: '#607f70',
        eventClick: null,
      },
    };
  },
  watch: {
    busqueda() {
      this.paginaActual = 1;
      this.pacienteSeleccionado = null;
    },
    tipoPacienteVista() {
      this.paginaActual = 1;
      this.pacienteSeleccionado = null;
      this.actualizarEventosCalendario();
    },
  },
  computed: {
    pacientesPorTipo() {
      return this.pacientes.filter(p =>
        (p.tipo_paciente || 'huesped') === this.tipoPacienteVista &&
        (this.esAdministrador || !p.oculto)
      );
    },
    pacientesFiltrados() {
      if (!this.busqueda.trim()) return this.pacientesPorTipo;
      const limpio = DOMPurify.sanitize(this.busqueda)
        .split('')
        .filter(c => c.charCodeAt(0) > 0x1F && c.charCodeAt(0) !== 0x7F)
        .join('')
        .toLowerCase();
      return this.pacientesPorTipo.filter(paciente =>
        Object.values(paciente).some(valor =>
          valor && valor.toString().toLowerCase().includes(limpio)
        )
      );
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.pacientesFiltrados.length / this.porPagina));
    },
    pacientesPaginados() {
      const inicio = (this.paginaActual - 1) * this.porPagina;
      return this.pacientesFiltrados.slice(inicio, inicio + this.porPagina);
    },
  },
  methods: {
    seleccionar(paciente) {
      if (this.editando) {
        Swal.fire('Cancelar edición', 'Primero guarda o cancela la edición actual.', 'warning');
        return;
      }
      this.pacienteSeleccionado =
        this.pacienteSeleccionado?.folio === paciente.folio ? null : paciente;
    },
    abrirModalCalendario(info) {
      this.pacienteModal = info.event.extendedProps.paciente;
    },
    actualizarSidebar(estado) {
      this.sidebarCerrado = estado;
    },
    async cargarPacientes() {
      this.cargando = true;
      const id_doctor = localStorage.getItem('id_doctor');
      if (!id_doctor) {
        this.cargando = false;
        return;
      }
      // El backend ya limita el resultado al doctor de la sesión cuando no es administrador.
      this.pacientes = await getConsultas();
      this.actualizarEventosCalendario();
      this.cargando = false;
    },

    actualizarEventosCalendario() {
      this.calendarOptions.events = this.pacientesPorTipo.map(p => ({
        id: p.folio,
        title: `${p.nombre} ${p.apellido_paterno}`,
        date: p.fecha,
        extendedProps: { paciente: p },
      }));
    },

    nombreMedico(idDoctor) {
      const medico = this.doctoresPorId[idDoctor];
      return medico ? (medico.nombre_completo || `${medico.nombre} ${medico.apellido_paterno}`.trim()) : '—';
    },
    detectarScroll() {
      const alturaDocumento = document.documentElement.scrollHeight;
      const alturaVentana = document.documentElement.clientHeight;
      const desplazamientoActual = window.pageYOffset || document.documentElement.scrollTop;
      this.estaEnElFinal = (desplazamientoActual + alturaVentana + 5) >= alturaDocumento;
    },
    toggleScroll() {
      window.scrollTo({
        top: this.estaEnElFinal ? 0 : document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    },

    async toggleOcultarExpediente() {
      if (!this.pacienteSeleccionado) return;
      const folio = this.pacienteSeleccionado.folio;
      const nuevoValor = !this.pacienteSeleccionado.oculto;
      try {
        await updateConsulta(folio, { oculto: nuevoValor });
        this.pacienteSeleccionado.oculto = nuevoValor;
        const idx = this.pacientes.findIndex(p => p.folio === folio);
        if (idx !== -1) this.pacientes[idx].oculto = nuevoValor;
        Swal.fire({
          icon: 'success',
          title: nuevoValor ? 'Expediente ocultado' : 'Expediente visible',
          text: nuevoValor
            ? 'Este expediente ya no será visible para hoteles ni colaboradores.'
            : 'Este expediente vuelve a ser visible para hoteles y colaboradores.',
          confirmButtonColor: '#133e68',
        });
      } catch (error) {
        Swal.fire('Error', error.message || 'No se pudo actualizar el expediente.', 'error');
      }
    },

    // ── Edición de consulta (punto 11) ──
    iniciarEdicion() {
      if (!this.esAdministrador) return;
      if (!this.pacienteSeleccionado) return;
      this.formularioEdicion = {
        nombre: this.pacienteSeleccionado.nombre || '',
        apellido_paterno: this.pacienteSeleccionado.apellido_paterno || '',
        apellido_materno: this.pacienteSeleccionado.apellido_materno || '',
        nombre_hotel: this.pacienteSeleccionado.nombre_hotel || '',
        sintomas: this.pacienteSeleccionado.sintomas || '',
        diagnostico: this.pacienteSeleccionado.diagnostico || '',
        observaciones: this.pacienteSeleccionado.observaciones || '',
      };
      this.editando = true;
    },

    cancelarEdicion() {
      this.editando = false;
      this.formularioEdicion = {
        nombre: '', apellido_paterno: '', apellido_materno: '', nombre_hotel: '',
        sintomas: '', diagnostico: '', observaciones: '',
      };
    },

    async guardarEdicion() {
      if (!this.pacienteSeleccionado) return;
      const folio = this.pacienteSeleccionado.folio;
      const nuevosDatos = {
        nombre: this.formularioEdicion.nombre.trim(),
        apellido_paterno: this.formularioEdicion.apellido_paterno.trim(),
        apellido_materno: this.formularioEdicion.apellido_materno.trim(),
        nombre_hotel: this.formularioEdicion.nombre_hotel.trim(),
        sintomas: this.formularioEdicion.sintomas.trim(),
        diagnostico: this.formularioEdicion.diagnostico.trim(),
        observaciones: this.formularioEdicion.observaciones.trim(),
      };
      // Validar mínimo
      if (!nuevosDatos.nombre) {
        Swal.fire('Campo requerido', 'El nombre del paciente no puede estar vacío.', 'warning');
        return;
      }
      this.guardando = true;
      try {
        await updateConsulta(folio, nuevosDatos);
        // Actualizar el objeto local
        Object.assign(this.pacienteSeleccionado, nuevosDatos);
        // También actualizar en la lista de pacientes (por si acaso)
        const idx = this.pacientes.findIndex(p => p.folio === folio);
        if (idx !== -1) Object.assign(this.pacientes[idx], nuevosDatos);
        // Refrescar eventos del calendario
        this.actualizarEventosCalendario();
        Swal.fire('Éxito', 'Los datos de la consulta se actualizaron correctamente.', 'success');
        this.editando = false;
        this.formularioEdicion = {
          nombre: '', apellido_paterno: '', apellido_materno: '', nombre_hotel: '',
          sintomas: '', diagnostico: '', observaciones: '',
        };
      } catch (error) {
        Swal.fire('Error', error.message || 'No se pudieron guardar los cambios.', 'error');
      } finally {
        this.guardando = false;
      }
    },
  },
  async mounted() {
    this.calendarOptions.eventClick = this.abrirModalCalendario;

    const [usuarios, me] = await Promise.all([getUsuarios(), getMe()]);
    this.doctoresPorId = Object.fromEntries(usuarios.map(u => [String(u.id), u]));
    this.esAdministrador = me.usuario?.rango === 'administrador';

    this.cargarPacientes();
    window.addEventListener('scroll', this.detectarScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.detectarScroll);
  },
};
</script>

<style scoped>
/* ---------- LAYOUT ---------- */
.dashboard-contenedor {
  display: flex;
  min-height: 100vh;
  background-color: var(--brand-beige);
}

.contenido {
  flex-grow: 1;
  padding: 24px;
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.contenido-expandido {
  margin-left: 80px;
}

/* ---------- BARRA DE HERRAMIENTAS ---------- */
.barra-herramientas {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.bht-input {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  border: 2px solid var(--brand-secondary-light);
  font-size: 15px;
  background: var(--white);
  color: var(--brand-secondary);
  outline: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.bht-input:focus {
  border-color: var(--brand-accent-sage);
  box-shadow: 0 0 0 4px rgba(96, 127, 112, 0.2);
}

.bht-input::placeholder {
  color: var(--brand-secondary-light);
}

.bht-spacer {
  flex: 1;
}

.switch-tipo-paciente {
  display: flex;
  border: 2px solid var(--brand-primary);
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
}

.switch-tipo-paciente button {
  padding: 9px 16px;
  border: none;
  background: var(--white);
  color: var(--brand-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
}

.switch-tipo-paciente button.active {
  background: var(--brand-primary);
  color: var(--white);
}

.btn-vista {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: 2px solid var(--brand-accent-sage);
  border-radius: 10px;
  background: var(--white);
  color: var(--brand-accent-sage);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;
}

.btn-vista:hover {
  background: var(--brand-accent-sage);
  color: var(--white);
}

.btn-vista .material-icons {
  font-size: 20px;
}

/* ---------- MASTER: TABLA ---------- */
.master-contenedor {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* wrapper con scroll horizontal en móvil */
.tabla-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--white);
  border-radius: 12px 12px 0 0;
  box-shadow: 0 4px 16px rgba(19, 62, 104, 0.10);
}

.tabla-pacientes {
  width: 100%;
  min-width: 520px; /* evita que las columnas se colapsen antes del scroll */
  border-collapse: collapse;
  background: var(--white);
}

.tabla-pacientes thead {
  background: var(--brand-primary);
  color: var(--white);
}

.tabla-pacientes th {
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  letter-spacing: 0.4px;
}

.tabla-pacientes tbody tr {
  border-bottom: 1px solid rgba(19, 62, 104, 0.08);
  cursor: pointer;
  transition: background 0.15s ease;
}

.tabla-pacientes tbody tr:last-child {
  border-bottom: none;
}

.tabla-pacientes tbody tr:hover {
  background: rgba(96, 127, 112, 0.08);
}

.tabla-pacientes tbody tr.fila-activa {
  background: rgba(96, 127, 112, 0.18);
}

.tabla-pacientes td {
  padding: 13px 16px;
  font-size: 14px;
  color: var(--brand-secondary);
}

/* ---------- PAGINACIÓN ---------- */
.paginacion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  background: var(--white);
  border-top: 1px solid rgba(19, 62, 104, 0.08);
  box-shadow: 0 4px 16px rgba(19, 62, 104, 0.10);
  border-radius: 0 0 12px 12px;
}

.btn-pagina {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--brand-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-pagina:hover:not(:disabled) {
  background: rgba(96, 127, 112, 0.12);
  border-color: var(--brand-accent-sage);
  color: var(--brand-accent-sage);
}

.btn-pagina-activa {
  background: var(--brand-accent-sage) !important;
  color: var(--white) !important;
  border-color: var(--brand-accent-sage) !important;
}

.btn-pagina:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.paginacion-info {
  margin-left: 12px;
  font-size: 13px;
  color: var(--brand-secondary-light);
}

/* ---------- DETAIL: PANEL ---------- */
.detalle-panel {
  background: var(--white);
  border-top: 3px solid var(--brand-accent-sage);
  border-radius: 0 0 12px 12px;
  box-shadow: 0 8px 24px rgba(19, 62, 104, 0.12);
  overflow: hidden;
}

.detalle-encabezado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--brand-primary);
  color: var(--white);
}

.detalle-nombre-edicion {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.detalle-nombre-edicion .form-control {
  width: auto;
  min-width: 130px;
  padding: 6px 10px;
  font-size: 14px;
}

.detalle-encabezado h4 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 12px;
}

.detalle-folio {
  font-size: 13px;
  font-weight: 400;
  opacity: 0.75;
}

.detalle-oculto-tag {
  margin-left: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #d33;
  background: rgba(211, 51, 51, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
}

.btn-editar-detalle {
  background: none;
  border: none;
  color: var(--white);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: opacity 0.2s, background 0.2s;
}
.btn-editar-detalle:hover {
  background: rgba(255, 255, 255, 0.15);
}
.btn-editar-detalle .material-icons {
  font-size: 20px;
}

.btn-cerrar-detalle {
  background: none;
  border: none;
  color: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.btn-cerrar-detalle:hover {
  opacity: 1;
}

.detalle-cuerpo {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detalle-seccion h5 {
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-accent-sage);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(96, 127, 112, 0.2);
}

.detalle-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 24px;
}

.detalle-grid-2col {
  grid-template-columns: 1fr 1fr;
}

.detalle-grid > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detalle-grid strong {
  font-size: 12px;
  color: var(--brand-secondary-light);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.detalle-grid span {
  font-size: 14px;
  color: var(--brand-secondary);
}

.detalle-observaciones {
  font-size: 14px;
  color: var(--brand-secondary);
  line-height: 1.6;
  margin: 0;
}

.detalle-acciones-edicion {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.detalle-acciones-edicion .btn-cancelar {
  padding: 8px 20px;
  border: 1px solid #d6e0eb;
  border-radius: 8px;
  background: var(--white);
  color: var(--brand-primary);
  cursor: pointer;
  font-size: 13px;
  transition: border-color 0.2s, background 0.2s;
}
.detalle-acciones-edicion .btn-cancelar:hover {
  background: #f0f4f8;
}
.detalle-acciones-edicion .btn-guardar {
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  background: var(--brand-primary);
  color: var(--white);
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s, transform 0.15s;
}
.detalle-acciones-edicion .btn-guardar:hover {
  transform: translateY(-1px);
}

/* ---------- TRANSICIÓN ---------- */
.detalle-fade-enter-active,
.detalle-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.detalle-fade-enter-from,
.detalle-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ---------- MENSAJES ---------- */
.mensaje-cargando,
.mensaje-vacio {
  color: var(--brand-primary);
  font-weight: 600;
  text-align: center;
  padding: 40px;
  font-size: 16px;
}

/* ---------- SCROLL BTN ---------- */
.btn-scroll {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background: var(--brand-accent-sage);
  color: var(--white);
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(96, 127, 112, 0.3);
  cursor: pointer;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
}

.btn-scroll:hover {
  background: var(--brand-accent-navy);
  transform: scale(1.1);
}

.btn-scroll span {
  font-size: 28px;
}

/* ---------- RESPONSIVE ---------- */
@media (max-width: 1024px) {
  .contenido { margin-left: 80px; }
}

@media (max-width: 768px) {
  .dashboard-contenedor { flex-direction: column; }

  .contenido {
    margin-left: 0 !important;
    padding: 12px;
    /* espacio para la navbar móvil fija */
    padding-top: 70px;
  }

  .barra-busqueda input { width: 95%; font-size: 14px; }

  /* paginación: ocultar info y compactar en móvil */
  .paginacion { flex-wrap: wrap; gap: 4px; padding: 10px 8px; }
  .paginacion-info { width: 100%; text-align: center; margin: 4px 0 0; }

  /* tabla: reducir padding */
  .tabla-pacientes th,
  .tabla-pacientes td { padding: 10px 8px; font-size: 13px; }

  /* detail panel: 1 columna */
  .detalle-grid { grid-template-columns: 1fr 1fr; }
  .detalle-grid-2col { grid-template-columns: 1fr; }

  .detalle-encabezado h4 { font-size: 14px; }
  .detalle-cuerpo { padding: 14px; gap: 14px; }
}

@media (max-width: 480px) {
  .contenido { padding: 8px; padding-top: 70px; }

  .bht-input { font-size: 13px; }

  .detalle-grid,
  .detalle-grid-2col { grid-template-columns: 1fr; }

  .btn-scroll { bottom: 16px; right: 16px; width: 42px; height: 42px; }
  .btn-scroll span { font-size: 22px; }
}

/* ---------- CALENDARIO ---------- */
.calendario-wrapper {
  background: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(19, 62, 104, 0.10);
  padding: 16px;
}

:deep(.fc-toolbar-title) {
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-primary);
  text-transform: capitalize;
}

:deep(.fc-button-primary) {
  background: var(--brand-primary) !important;
  border-color: var(--brand-primary) !important;
  font-size: 13px;
}

:deep(.fc-button-primary:hover) {
  background: var(--brand-accent-sage) !important;
  border-color: var(--brand-accent-sage) !important;
}

:deep(.fc-button-primary:not(:disabled).fc-button-active) {
  background: var(--brand-accent-sage) !important;
  border-color: var(--brand-accent-sage) !important;
}

:deep(.fc-event) {
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

:deep(.fc-daygrid-day-number) {
  color: var(--brand-secondary);
  font-size: 13px;
}

:deep(.fc-col-header-cell) {
  background: var(--brand-primary);
}

:deep(.fc-col-header-cell-cushion) {
  color: var(--white);
  font-weight: 600;
  font-size: 13px;
}

/* ---------- MODAL CALENDARIO ---------- */
.modal-paciente-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-paciente {
  background: var(--white);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-paciente-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--brand-primary);
  color: var(--white);
  flex-shrink: 0;
}

.modal-paciente-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.modal-scroll {
  overflow-y: auto;
}

/* transición modal */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-active .modal-paciente,
.modal-fade-leave-active .modal-paciente {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .modal-paciente,
.modal-fade-leave-to .modal-paciente {
  transform: scale(0.96);
  opacity: 0;
}
</style>
